import { FilterByARIndex } from "../../models/accountsReceivable";
import { CoreTrackingFilterBy } from "../../models/coreTracking";
import {
  AccessLevel,
  Application,
  ClientStatus,
  Color,
  CustomerSignatureLine,
  LogoPlacement,
  MiscellaneousChargeStatus,
  PromisedDateOptions,
  RateTypes,
  ReportType,
  SalesTaxOptionType,
  SettingsInventoryNotifyType,
  TermEnum,
  TicketType,
} from "../../models/enums";
import { SettingsStatus } from "../../models/settings";
import { DeletedTicketSessionSortKeys } from "../../models/systemAdmin";
import { OrderDirection, RecentTicketFilterKeys, RecentTicketSortKeys } from "../../models/ticket";
import i18n from "../i18n/i18n";

export interface Options {
  text: string;
  value: string | number;
}

export const hourlyRateOptions: Options[] = [
  {
    text: i18n.t("ticket_defaults.flatandhourly", { ns: "ticketSettings" }),
    value: RateTypes.Flat | RateTypes.Hourly,
  },
  {
    text: i18n.t("ticket_defaults.flat", { ns: "ticketSettings" }),
    value: RateTypes.Flat,
  },
  {
    text: i18n.t("ticket_defaults.hourly", { ns: "ticketSettings" }),
    value: RateTypes.Hourly,
  },
];

export const promisedDateOptions: Options[] = [
  {
    text: i18n.t("ticket_defaults.blank", { ns: "ticketSettings" }),
    value: PromisedDateOptions.Blank,
  },
  {
    text: i18n.t("ticket_defaults.today", { ns: "ticketSettings" }),
    value: PromisedDateOptions.Today,
  },
  {
    text: i18n.t("ticket_defaults.tomorrow", { ns: "ticketSettings" }),
    value: PromisedDateOptions.Tomorrow,
  },
];

export const yesNoOptions: Options[] = [
  { text: i18n.t("yes", { ns: "common" }), value: "true" },
  { text: i18n.t("no", { ns: "common" }), value: "false" },
];

export const workFlowOptions: Options[] = [
  {
    text: i18n.t("ticket_path.begin", { ns: "workflowSettings" }),
    value: "true",
  },
  {
    text: i18n.t("ticket_path.end", { ns: "workflowSettings" }),
    value: "false",
  },
];

export const defaultTicketOptions: Options[] = [
  {
    text: i18n.t("ticket_path.invoice", { ns: "workflowSettings" }),
    value: TicketType.Invoice.toString(),
  },
  {
    text: i18n.t("ticket_path.estimate", { ns: "workflowSettings" }),
    value: TicketType.Estimate.toString(),
  },
];

export const assignmentOptions: Options[] = [
  { text: i18n.t("tech.labor", { ns: "miscSettings" }), value: "false" },
  { text: i18n.t("tech.laborandparts", { ns: "miscSettings" }), value: "true" },
];

export const taxOptions: Options[] = [
  {
    text: i18n.t('taxOptions.parts', { ns: 'pricingSettings' }),
    value: SalesTaxOptionType.Parts,
  },
  {
    text: i18n.t('taxOptions.labor', { ns: 'pricingSettings' }),
    value: SalesTaxOptionType.Labor,
  },
  {
    text: i18n.t('taxOptions.misc', { ns: 'pricingSettings' }),
    value: SalesTaxOptionType.ExemptMisc,
  },
  {
    text: i18n.t('taxOptions.fl', { ns: 'pricingSettings' }),
    value: SalesTaxOptionType.OnTicket,
  },
];

export const miscChargeStatusOptions: Options[] = [
  {
    text: i18n.t("always", { ns: "miscSettings" }),
    value: MiscellaneousChargeStatus.Always,
  },
  {
    text: i18n.t("alwaysask", { ns: "miscSettings" }),
    value: MiscellaneousChargeStatus.AlwaysAsk,
  },
  {
    text: i18n.t("off", { ns: "common" }),
    value: MiscellaneousChargeStatus.Off,
  },
];

export const quickVinOptions: Options[] = [
  {
    text: i18n.t("quickvin.enable", { ns: "advancedSettings" }),
    value: "true",
  },
  {
    text: i18n.t("quickvin.disable", { ns: "advancedSettings" }),
    value: "false",
  },
];

export const signatureOptions: Options[] = [
  {
    text: i18n.t("formatting.underline", { ns: "ticketSettings" }),
    value: CustomerSignatureLine.Underline,
  },
  {
    text: i18n.t("formatting.box", { ns: "ticketSettings" }),
    value: CustomerSignatureLine.Box,
  },
];
export const logoOptions: Options[] = [
  {
    text: i18n.t("formatting.left", { ns: "ticketSettings" }),
    value: LogoPlacement.Left,
  },
  {
    text: i18n.t("formatting.center", { ns: "ticketSettings" }),
    value: LogoPlacement.Center,
  },
];

export const notesOptions: Options[] = [
  {
    text: i18n.t("formatting.shownoteslabel", { ns: "ticketSettings" }),
    value: "true",
  },
  {
    text: i18n.t("formatting.hidenoteslabel", { ns: "ticketSettings" }),
    value: "false",
  },
];

export const reportTypeOptions: Options[] = [
  { text: i18n.t("detailed", { ns: "reports" }), value: ReportType.Detailed },
  { text: i18n.t("summary", { ns: "reports" }), value: ReportType.Summary },
];
export const yearOptions: Options[] = [
  { text: i18n.t("customs.singleYear", { ns: "workflowSettings" }), value: 1 },
  { text: i18n.t("customs.range", { ns: "workflowSettings" }), value: 2 },
  { text: i18n.t("customs.all", { ns: "workflowSettings" }), value: 3 },
];

export const internalAccessLevelOptions: Options[] = [
  { text: i18n.t("staff", { ns: "abandoned" }), value: AccessLevel.Staff },
  {
    text: i18n.t("staff.manager", { ns: "users" }),
    value: AccessLevel.Manager,
  },
  {
    text: i18n.t("staff.accountOwner", { ns: "users" }),
    value: AccessLevel.Owner,
  }
];


export const accessLevelOptions: Options[] = [
  { text: i18n.t("staff", { ns: "abandoned" }), value: AccessLevel.Staff },
  {
    text: i18n.t("staff.manager", { ns: "users" }),
    value: AccessLevel.Manager,
  },
  {
    text: i18n.t("staff.accountOwner", { ns: "users" }),
    value: AccessLevel.Owner,
  },
];

export const enableOptions: Options[] = [
  {
    text: i18n.t("enabled", { ns: "miscSettings" }),
    value: "true",
  },
  {
    text: i18n.t("disabled", { ns: "miscSettings" }),
    value: "false",
  },
];

export const packageJobOptions: Options[] = [
  {
    text: i18n.t("pops.use_save_values", { ns: "tickets" }),
    value: "true",
  },
  {
    text: i18n.t("pops.match_vehicle", { ns: "tickets" }),
    value: "false",
  },
];

export const serviceTypeOptions = [
  {
    text: i18n.t("service_type.realTimePro", { ns: "common" }),
    value: Application.RealTimeLaborGuidePro,
  },
  {
    text: i18n.t("service_type.realTimeGuide", { ns: "common" }),
    value: Application.RealTimeLaborGuide,
  },
  {
    text: i18n.t("service_type.automotiveExpert", { ns: "common" }),
    value: Application.AutomotiveExpert,
  },
];

export const subscriptionServiceTypeOptions = [
  {
    text: i18n.t("service_type.realTimePro", { ns: "common" }),
    value: Application.RealTimeLaborGuidePro,
  },
  {
    text: i18n.t("service_type.realTimeGuide", { ns: "common" }),
    value: Application.RealTimeLaborGuide,
  },
];

export const planIntervalOptions = [
  {
    text: i18n.t("subscription.monthly", { ns: "subscription" }),
    value: TermEnum.Monthly,
  },
  {
    text: i18n.t("subscription.quarterly", { ns: "subscription" }),
    value: TermEnum.Quarterly,
  },
  {
    text: i18n.t("subscription.annual", { ns: "subscription" }),
    value: TermEnum.Annual,
  },
];

//TODO: i18n?
export const clientStatusOptions: Options[] = [
  {
    text: "Terminated-Dnr",
    value: ClientStatus.TerminatedDnr,
  },
  {
    text: "Declined",
    value: ClientStatus.Declined,
  },
  {
    text: "Deactivated",
    value: ClientStatus.Deactivated,
  },

  {
    text: "Sign Up InProgress",
    value: ClientStatus.Active | ClientStatus.SignUpInProgress,
  },
  {
    text: "PreTrial",
    value: ClientStatus.PreTrial,
  },
  // {
  //   text: "Trial",
  //   value: ClientStatus.Trial,
  // },
  // {
  //   text: "Active",
  //   value: ClientStatus.Active,
  // },
  // {
  //   text: "Subscription",
  //   value: ClientStatus.Subscription,
  // },
  // {
  //   text: "SubscriptionExtension",
  //   value: ClientStatus.SubscriptionExtension,
  // },
  {
    text: "Inactive With Expired Trial",
    value: ClientStatus.Expired | ClientStatus.Trial,
  },
  {
    text: "Inactive With Expired Subscription",
    value:
     ClientStatus.Expired | ClientStatus.Subscription,
  },
  {
    text: "Inactive With Expired Subscription Extension",
    value:
      ClientStatus.Expired |
      ClientStatus.SubscriptionExtension,
  },
  {
    text: "Active With Trial",
    value: ClientStatus.Active | ClientStatus.Trial,
  },
  {
    text: "Active With Subscription",
    value: ClientStatus.Active | ClientStatus.Subscription,
  },
  {
    text: "Active With Subscription Extension",
    value: ClientStatus.Active | ClientStatus.SubscriptionExtension,
  },
  {
    text: "Active With Trial And Expired Subscription",
    value:
      ClientStatus.Active |
      ClientStatus.Trial |
      ClientStatus.Expired |
      ClientStatus.Subscription,
  },
  {
    text: "Active With Trial And Expired Subscription Extension",
    value:
      ClientStatus.Active |
      ClientStatus.Trial |
      ClientStatus.Expired |
      ClientStatus.SubscriptionExtension,
  },
];

export const returnPartsOption: Options[] = [
  {
    text: i18n.t("returned", { ns: "tickets" }),
    value: "true",
  },
  {
    text: i18n.t("not_returned", { ns: "tickets" }),
    value: "false",
  },
];

export const areaOption= [{ text: "Login", value: 0 }];

export const colorOptions = [
  { text: "Negative/Error (Red)", value: Color.red },
  { text: "Positive/Success (Green)", value: Color.green },
  { text: "Info (Blue)", value: Color.blue },
  { text: "Warning (Yellow)", value: Color.yellow }
]

export const selectableCommentStatusOptions = [
  { text: "Remove", value: SettingsStatus.Remove },
  { text: "Off", value: SettingsStatus.Off },
  { text: "Always Ask", value: SettingsStatus.AlwaysAsk },
  { text: "Always", value: SettingsStatus.Always }
]
export const recentTicketsSortOptions = [
  { text: "Ticket Date", value: RecentTicketSortKeys.TicketDate },
  { text: "Ticket Number", value: RecentTicketSortKeys.TicketNumber },
  { text: "Total Amount", value: RecentTicketSortKeys.TotalAmount },
];
export const recentTicketsFilterOptions = [
  { text: "All", value: RecentTicketFilterKeys.All },
  { text: "Estimate", value: RecentTicketFilterKeys.Estimate },
  { text: "Invoice", value: RecentTicketFilterKeys.Invoice },
];

export const sortOrderOptions = [
  { text: "High to Low", value: OrderDirection.Descending },
  { text: "Low to High", value: OrderDirection.Ascending },
]

export const sortOrderDateOptions = [
  { text: "Newest First", value: OrderDirection.Descending },
  { text: "Oldest First", value: OrderDirection.Ascending },
]

export const sortOrderNameOptions = [
  { text: "A → Z", value: OrderDirection.Ascending },
  { text: "Z → A", value: OrderDirection.Descending },
]

export const sortOrderTicketTypeOptions = [
  { text: "Invoice First", value: OrderDirection.Ascending },
  { text: "Estimate First", value: OrderDirection.Descending },
]

export const deletedTicketSessionSortByOptions = [
  { text: "Deleted Date", value: DeletedTicketSessionSortKeys.DeletedDate, orderOpts: sortOrderDateOptions },
  { text: "Customer Name", value: DeletedTicketSessionSortKeys.CustomerName, orderOpts: sortOrderNameOptions },
  { text: "Ticket Number", value: DeletedTicketSessionSortKeys.TicketNumber, orderOpts: sortOrderOptions },
  { text: "Ticket Type", value: DeletedTicketSessionSortKeys.TicketType, orderOpts: sortOrderTicketTypeOptions },
];

export function getAccessOptionList(accessLevel: AccessLevel): Options[] {
  return accessLevelOptions.filter((x) => {
    return Number(x.value) <= accessLevel;
  });
}
export const inventoryReminderTimesOptions = [
  { text: "Every Weekday", value: SettingsInventoryNotifyType.EveryWeekday },
  { text: "Every Two Days", value: SettingsInventoryNotifyType.EveryTwoDays },
  { text: "Every Three Days", value: SettingsInventoryNotifyType.EveryThreeDays },
  { text: "Every Four Days", value: SettingsInventoryNotifyType.EveryFourDays },
  { text: "Every Five Days", value: SettingsInventoryNotifyType.EveryFiveDays },
  { text: "Every Week", value: SettingsInventoryNotifyType.EveryWeek  }
];

export const coreTrackingFilterByOptions = [
  { text: "Created Date", value: CoreTrackingFilterBy.CreatedDate },
  { text: "Return Date", value: CoreTrackingFilterBy.ReturnDate },
  { text: "Refund Date", value: CoreTrackingFilterBy.RefundDate },
  { text: "Invoice Date", value: CoreTrackingFilterBy.InvoiceDate }
];

export const arIndexFilterByOptions = [
  { text: "All", value: FilterByARIndex.All },
  { text: "Paid", value: FilterByARIndex.Paid },
  { text: "Not Paid", value: FilterByARIndex.NotPaid }
];